/**
 * OverviewPage — high-level admin business dashboard. First landing point in
 * the admin nav. Surfaces daily KPIs plus a 12-month revenue trend.
 *
 * V1 metrics:
 *   Top row (ops):
 *     - Total Orders          (→ /admin)
 *     - In Progress           (→ /admin?status=in_progress)
 *     - Completed             (→ /admin?status=completed)
 *     - New Orders This Month (→ /admin filtered by created-this-month)
 *   Financial row:
 *     - Total Active Order Value        (opens breakdown modal)
 *     - Open Uninvoiced Order Value     (opens breakdown modal)
 *     - Average Order Value (12mo)      (opens active breakdown modal)
 *   Customer row:
 *     - Active Customers      (→ /admin/companies)
 *
 * Followed by:
 *   - Monthly revenue trend (last 12 months, area chart) — booked/invoiced
 *     revenue derived from order.invoice_total when the order has been
 *     invoiced. Falls back to order_total_eur when invoice_total missing.
 *
 * Layout mirrors Finance / Orders modules: outer <div bg-background> + Header
 * + max-w-7xl main container with a text-2xl bold h1.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersApi } from '../../lib/api';
import { Header } from '../../components/Header';
import { Card } from '../../components/ui/card';
import { OrderValueBreakdownDialog } from '../../components/OrderValueBreakdownDialog';
import {
    Package,
    Clock,
    CheckCircle,
    FileText,
    AlertCircle,
    Loader2,
    LayoutDashboard,
    Users,
    Sparkles,
    TrendingUp,
    Calculator,
    Trophy,
    Percent,
    ShoppingBag,
} from 'lucide-react';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    ComposedChart,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from 'recharts';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';

const COMPLETED_STATUSES = ['Delivered/Closed', 'Completed', 'Closed'];
const CANCELLED_STATUSES = ['Cancelled'];
const DEFAULT_DKK_PER_EUR = 7.46;

// ─── Forecast helpers ──────────────────────────────────────────────
// Default production lead time, used when neither a confirmed supplier
// delivery date nor a still-realistic customer-requested date exists.
// Vinyl ≈ 8 weeks (56 days) is a conservative Sony-era estimate; CDs
// and digital-only orders use shorter windows. We intentionally err on
// the longer side so forecast revenue is never pulled forward past
// what the plant can realistically ship.
const FORECAST_LEAD_TIME_DAYS = {
    vinyl: 56,
    cd: 35,
    dvd: 35,
    bluray: 35,
};
const DEFAULT_FORECAST_LEAD_TIME_DAYS = 56;

// Statuses that COULD still contribute to future revenue — i.e. a
// customer-accepted order whose revenue has not yet landed in actuals.
// We keep the list conservative: anything in Quote/Quoted/Draft is
// still at the proposal stage; cancelled or completed orders no
// longer belong in the pipeline.
const FORECAST_STATUSES = new Set([
    'Submitted',
    'In Progress',
    'In Production',
    'in_production',
    'On Hold',
    'Shipping',
]);

const monthKey = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const monthLabel = (d) =>
    d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });

// Resolve the expected recognition date for a single order using the
// hierarchy mandated by the forecast rules:
//   1. Supplier-confirmed delivery date (most trustworthy).
//   2. Customer-requested delivery date, but only if it's today or
//      later. Overdue requested dates are discarded and we fall
//      through to the lead-time estimate — otherwise we'd anchor
//      next quarter's revenue on a date that has already passed.
//   3. created_at + lead-time days for the order's product_type.
// Returns ``null`` when no reliable estimate is available (we exclude
// the order rather than inventing a date, per the brief).
const resolveExpectedDeliveryDate = (order, now) => {
    const _parse = (raw) => {
        if (!raw) return null;
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? null : d;
    };

    // 1. Supplier/customer delivery confirmed by the supplier.
    const supplierConfirmed = _parse(
        order?.supplier_confirmation?.customer_delivery_date
        || order?.supplier_confirmation?.delivery_date
    );
    if (supplierConfirmed) return supplierConfirmed;

    // 2. Customer-requested delivery date (if still realistic).
    const requested = _parse(order?.requested_delivery_date);
    if (requested && requested >= now) return requested;

    // 3. Lead-time estimate from the order creation (or current date
    // if the creation date is missing for some odd legacy row).
    const createdAt = _parse(order?.created_at || order?.createdAt);
    const anchor = createdAt || now;
    const lead = FORECAST_LEAD_TIME_DAYS[(order?.product_type || '').toLowerCase()]
        ?? DEFAULT_FORECAST_LEAD_TIME_DAYS;
    const estimated = new Date(anchor.getTime());
    estimated.setDate(estimated.getDate() + lead);
    return estimated;
};

// EUR revenue for a single order using the SAME rules as the actual
// trend: invoice_total when it's set + currency-converted, otherwise
// the frozen `order_total_eur` snapshot. See the actual-revenue
// aggregator comment above for the historical equivalence.
const resolveOrderRevenueEur = (order) => {
    if (order?.invoice_total) {
        if (order.invoice_currency === 'EUR') return order.invoice_total;
        if (order.invoice_currency === 'DKK')
            return order.invoice_total / DEFAULT_DKK_PER_EUR;
        return order.order_total_eur || 0;
    }
    return order?.order_total_eur || 0;
};

// ─── Small building blocks ──────────────────────────────────────────

const KpiCard = ({ icon: Icon, iconBg, iconColor, label, value, sub, onClick, testId }) => {
    const clickable = typeof onClick === 'function';
    return (
        <Card
            className={`metric-card ${clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
            onClick={onClick}
            role={clickable ? 'button' : undefined}
            data-testid={testId}
        >
            <div className="flex items-center gap-2.5">
                <div className={`p-1.5 rounded-lg ${iconBg}`}>
                    <Icon className={`h-4 w-4 ${iconColor}`} />
                </div>
                <div className="min-w-0">
                    <p className="text-xl font-bold text-slate-900 leading-tight truncate">{value}</p>
                    <p className="text-[11px] text-muted-foreground leading-tight">{label}</p>
                    {sub && <p className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</p>}
                </div>
            </div>
        </Card>
    );
};

// ─── Page ───────────────────────────────────────────────────────────

const OverviewPage = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    // Product/media category filter — segmented selector under the header.
    // `null` == All (no filter). Backend applies the filter at the query
    // level via `?product_type=…` so the frontend never sees rows that
    // don't belong to the selected category.
    const [category, setCategory] = useState(null);

    // Performance Trend metric selector — swaps the chart's series without
    // adding another page-level filter. Independent from the category
    // filter (switching one never resets the other).
    const [metric, setMetric] = useState('revenue');

    // Performance Trend date basis. `order_date` (default) buckets by the
    // order's `created_at`; `invoice_date` buckets by the invoice's real
    // date (from OrderResponse.invoice_date — see resolver in
    // backend/routes/orders.py). Orders without a reliable invoice date
    // are excluded from the `invoice_date` chart — no faked dates.
    const [dateBasis, setDateBasis] = useState('order_date');

    // Breakdown modal state
    const [breakdownOpen, setBreakdownOpen] = useState(false);
    const [breakdownVariant, setBreakdownVariant] = useState('active');
    // Drill-down for a forecast month — carries the full
    // revenueForecast[] entry so the dialog can list the contributing
    // orders without re-querying the server. ``null`` keeps the
    // dialog closed.
    const [forecastDialogBucket, setForecastDialogBucket] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const res = await ordersApi.getAll(category ? { product_type: category } : undefined);
                if (!cancelled) setOrders(res.data || []);
            } catch {
                if (!cancelled) setOrders([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [category]);

    const metrics = useMemo(() => {
        const active = { dkk: 0, eur: 0, count: 0, details: [], gross_profit_eur: 0, units: 0, costed_orders: 0 };
        const uninvoiced = { dkk: 0, eur: 0, count: 0, details: [], gross_profit_eur: 0, units: 0, costed_orders: 0 };
        // Completed-only aggregator for Margin card B (revenue-weighted margin
        // for completed orders that also have a costed profit).
        const completedAgg = { revenue_eur: 0, gross_profit_eur: 0, count: 0 };
        let inProgress = 0;
        let completed = 0;
        const activeCustomerIds = new Set();

        // Last 12 months revenue buckets keyed by "YYYY-MM"
        const now = new Date();
        const trend = [];
        for (let i = 11; i >= 0; i -= 1) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            trend.push({
                key,
                label: d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
                revenue_eur: 0,
                orders_count: 0,
                gross_profit_eur: 0,
                units: 0,
                // Numerator/denominator for revenue-weighted margin:
                //   margin_pct = Σ gross_profit_eur / Σ net_revenue_eur × 100
                // Mathematically identical to the previous
                // Σ(gross_margin_pct × eur) / Σeur formulation, but sourced
                // from the same numbers we already accumulate.
                margin_pct: null,
            });
        }
        const trendByKey = Object.fromEntries(trend.map((t) => [t.key, t]));

        // Parallel 12-month buckets, indexed by the order's `invoice_date`
        // instead of `created_at`. Same shape and same math as `trend` —
        // just a different bucketing key. Only orders whose backend
        // resolver returned a non-null `invoice_date` land here.
        const invoiceTrend = trend.map((t) => ({
            key: t.key,
            label: t.label,
            revenue_eur: 0,
            orders_count: 0,
            gross_profit_eur: 0,
            units: 0,
            margin_pct: null,
        }));
        const invoiceTrendByKey = Object.fromEntries(invoiceTrend.map((t) => [t.key, t]));

        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        let newOrdersThisMonth = 0;

        // Rolling 12-month window (start of month, 11 months back).
        const trendWindowStart = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        const customerAgg = new Map(); // id → { name, id, revenue_eur, orders }

        orders.forEach((o) => {
            if (COMPLETED_STATUSES.includes(o.status)) completed += 1;
            if (!['Draft', ...COMPLETED_STATUSES, ...CANCELLED_STATUSES].includes(o.status)) {
                inProgress += 1;
            }
            if (!CANCELLED_STATUSES.includes(o.status) && o.status !== 'Draft') {
                if (o.customer_id || o.organization_id || o.company_id) {
                    activeCustomerIds.add(o.customer_id || o.organization_id || o.company_id);
                }
            }

            // Created-at based metrics
            const createdRaw = o.created_at || o.createdAt || null;
            if (createdRaw) {
                const created = new Date(createdRaw);
                if (!Number.isNaN(created.getTime())) {
                    if (created >= startOfMonth) newOrdersThisMonth += 1;
                    const key = `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}`;
                    if (trendByKey[key]) {
                        // Revenue proxy: use invoice_total when currency is set,
                        // otherwise order_total_eur (frozen at submit time).
                        let eur = 0;
                        if (o.invoice_total) {
                            if (o.invoice_currency === 'EUR') eur = o.invoice_total;
                            else if (o.invoice_currency === 'DKK') eur = o.invoice_total / DEFAULT_DKK_PER_EUR;
                            else eur = o.order_total_eur || 0;
                        } else {
                            eur = o.order_total_eur || 0;
                        }
                        trendByKey[key].revenue_eur += eur;
                        trendByKey[key].orders_count += 1;
                        trendByKey[key].units += Number(o.quantity) || 0;
                        // Only add profit for orders with a numeric value and
                        // positive net revenue — mirrors the Finance Summary
                        // rule (skip uncosted / zero-value orders instead of
                        // dragging the ratio down with a null).
                        if (o.gross_profit_eur != null && eur > 0) {
                            trendByKey[key].gross_profit_eur += o.gross_profit_eur;
                        }
                    }

                    // Aggregate per customer for the last 12 months. Collects
                    // every dimension so Top Customers can rank by any of the
                    // five metrics using the SAME numbers as the KPI row and
                    // Performance Trend. No parallel logic anywhere else.
                    if (created >= trendWindowStart) {
                        const cid = o.organization_id || o.customer_id || null;
                        const cname = o.organization_name || o.customer_name || 'Unknown';
                        const key2 = cid || `name:${cname}`;
                        let eur2 = 0;
                        if (o.invoice_total) {
                            if (o.invoice_currency === 'EUR') eur2 = o.invoice_total;
                            else if (o.invoice_currency === 'DKK') eur2 = o.invoice_total / DEFAULT_DKK_PER_EUR;
                            else eur2 = o.order_total_eur || 0;
                        } else {
                            eur2 = o.order_total_eur || 0;
                        }
                        const entry = customerAgg.get(key2) || {
                            id: cid,
                            name: cname,
                            revenue_eur: 0,
                            gross_profit_eur: 0,
                            units: 0,
                            orders: 0,
                            costed_orders: 0,   // for Margin qualification
                        };
                        entry.revenue_eur += eur2;
                        entry.orders += 1;
                        entry.units += Number(o.quantity) || 0;
                        if (o.gross_profit_eur != null && eur2 > 0) {
                            entry.gross_profit_eur += o.gross_profit_eur;
                            entry.costed_orders += 1;
                        }
                        customerAgg.set(key2, entry);
                    }
                }
            }

            // Invoice-date bucketing — parallel to the created-at logic
            // above, but keyed by `o.invoice_date` (resolved server-side
            // in backend/routes/orders.py::_resolve_invoice_date). Orders
            // without an invoice_date silently skip this loop so the
            // Performance Trend "Invoice date" mode NEVER falls back to
            // the order-created date — no faked dates.
            if (o.invoice_date) {
                const invDate = new Date(o.invoice_date);
                if (!Number.isNaN(invDate.getTime())) {
                    const invKey = `${invDate.getFullYear()}-${String(invDate.getMonth() + 1).padStart(2, '0')}`;
                    if (invoiceTrendByKey[invKey]) {
                        // Revenue rule mirrors the created-at branch above
                        // so a metric's absolute values match when the two
                        // dates fall in the same month.
                        let eurI = 0;
                        if (o.invoice_total) {
                            if (o.invoice_currency === 'EUR') eurI = o.invoice_total;
                            else if (o.invoice_currency === 'DKK') eurI = o.invoice_total / DEFAULT_DKK_PER_EUR;
                            else eurI = o.order_total_eur || 0;
                        } else {
                            eurI = o.order_total_eur || 0;
                        }
                        invoiceTrendByKey[invKey].revenue_eur += eurI;
                        invoiceTrendByKey[invKey].orders_count += 1;
                        invoiceTrendByKey[invKey].units += Number(o.quantity) || 0;
                        if (o.gross_profit_eur != null && eurI > 0) {
                            invoiceTrendByKey[invKey].gross_profit_eur += o.gross_profit_eur;
                        }
                    }
                }
            }

            // Order value pipeline (drops draft + cancelled)
            if (o.status === 'Draft' || o.status === 'Cancelled') return;

            const iStatus = o.invoice_status || null;
            const eStatus = o.invoice_economic_status || null;
            let invoice_state;
            let inclusion_reason;
            let isUninvoiced = false;
            if (eStatus === 'booked_in_economic' || iStatus === 'booked' || iStatus === 'paid') {
                invoice_state = 'booked';
                inclusion_reason = 'Booked in e-conomic — tracked in Finance (outstanding/paid)';
            } else if (eStatus === 'draft_in_economic') {
                invoice_state = 'economic_draft';
                inclusion_reason = 'E-conomic draft invoice created';
            } else if (eStatus === 'local_draft' || iStatus === 'draft' || iStatus === 'ready_to_book') {
                invoice_state = 'local_draft';
                inclusion_reason = 'Local draft sales document created';
            } else {
                invoice_state = 'none';
                inclusion_reason = 'No sales document — still to be invoiced';
                isUninvoiced = true;
            }

            let dkk = 0;
            let eur = 0;
            if (o.invoice_total) {
                if (o.invoice_currency === 'DKK') {
                    dkk = o.invoice_total;
                    eur = o.invoice_total / DEFAULT_DKK_PER_EUR;
                } else if (o.invoice_currency === 'EUR') {
                    eur = o.invoice_total;
                    dkk = o.invoice_total * DEFAULT_DKK_PER_EUR;
                }
            } else {
                dkk = o.order_total_local || 0;
                eur = o.order_total_eur || 0;
            }

            const detail = {
                id: o.id,
                order_number: o.order_number,
                title: o.title,
                status: o.status,
                dkk,
                eur,
                invoice_state,
                inclusion_reason,
                gross_profit_eur: o.gross_profit_eur ?? null,
                gross_margin_pct: o.gross_margin_pct ?? null,
                gross_margin_reason: o.gross_margin_reason ?? null,
            };

            active.dkk += dkk;
            active.eur += eur;
            active.count += 1;
            active.units += Number(o.quantity) || 0;
            if (o.gross_profit_eur != null && eur > 0) {
                active.gross_profit_eur += o.gross_profit_eur;
                active.costed_orders += 1;
            }
            active.details.push(detail);

            // Completed subset — used by Margin Card B.
            if (COMPLETED_STATUSES.includes(o.status) && o.gross_profit_eur != null && eur > 0) {
                completedAgg.revenue_eur += eur;
                completedAgg.gross_profit_eur += o.gross_profit_eur;
                completedAgg.count += 1;
            }

            if (isUninvoiced) {
                uninvoiced.dkk += dkk;
                uninvoiced.eur += eur;
                uninvoiced.count += 1;
                uninvoiced.units += Number(o.quantity) || 0;
                if (o.gross_profit_eur != null && eur > 0) {
                    uninvoiced.gross_profit_eur += o.gross_profit_eur;
                    uninvoiced.costed_orders += 1;
                }
                uninvoiced.details.push(detail);
            }
        });

        active.details.sort((a, b) => (b.dkk || 0) - (a.dkk || 0));
        uninvoiced.details.sort((a, b) => (b.dkk || 0) - (a.dkk || 0));

        // Finalise the weighted margin per month using the raw
        // revenue + profit sums. Months with zero valid revenue leave
        // margin_pct null so the chart hides that datapoint.
        trend.forEach((row) => {
            row.margin_pct = row.revenue_eur > 0
                ? (row.gross_profit_eur / row.revenue_eur) * 100
                : null;
        });
        invoiceTrend.forEach((row) => {
            row.margin_pct = row.revenue_eur > 0
                ? (row.gross_profit_eur / row.revenue_eur) * 100
                : null;
        });

        const totalRevenue12m = trend.reduce((s, m) => s + m.revenue_eur, 0);
        const totalGrossProfit12m = trend.reduce((s, m) => s + m.gross_profit_eur, 0);
        const totalUnits12m = trend.reduce((s, m) => s + m.units, 0);
        const totalCount12m = trend.reduce((s, m) => s + m.orders_count, 0);
        const avgOrderValue12m = totalCount12m > 0 ? totalRevenue12m / totalCount12m : 0;
        const avgGrossProfit12m = totalCount12m > 0 ? totalGrossProfit12m / totalCount12m : 0;
        const avgUnitsPerOrder12m = totalCount12m > 0 ? totalUnits12m / totalCount12m : 0;
        const avgOrdersPerMonth12m = totalCount12m / 12;
        const weightedMargin12m = totalRevenue12m > 0 ? (totalGrossProfit12m / totalRevenue12m) * 100 : null;
        const completedMargin = completedAgg.revenue_eur > 0
            ? (completedAgg.gross_profit_eur / completedAgg.revenue_eur) * 100
            : null;
        // Recent Margin (3mo) — same weighted formula on the most recent 3 months.
        const recent3 = trend.slice(-3);
        const recent3Rev = recent3.reduce((s, m) => s + m.revenue_eur, 0);
        const recent3Gp = recent3.reduce((s, m) => s + m.gross_profit_eur, 0);
        const recentMargin3m = recent3Rev > 0 ? (recent3Gp / recent3Rev) * 100 : null;

        // Top Customers — five sort variants, all derived from the same
        // customerAgg map. Margin variant additionally applies the
        // qualification rule (≥2 costed orders AND ≥€2 500 net revenue).
        const allCustomers = Array.from(customerAgg.values());
        const rankBy = (getter) => allCustomers
            .filter((c) => (getter(c) ?? 0) > 0)
            .sort((a, b) => (getter(b) ?? 0) - (getter(a) ?? 0))
            .slice(0, 5);
        const marginQualified = allCustomers.filter(
            (c) => c.costed_orders >= 2 && c.revenue_eur >= 2500
        );
        const topByMetric = {
            revenue:      rankBy((c) => c.revenue_eur),
            gross_profit: rankBy((c) => c.gross_profit_eur),
            units:        rankBy((c) => c.units),
            orders:       rankBy((c) => c.orders),
            margin: marginQualified
                .map((c) => ({
                    ...c,
                    margin_pct: c.revenue_eur > 0 ? (c.gross_profit_eur / c.revenue_eur) * 100 : null,
                }))
                .sort((a, b) => (b.margin_pct ?? -Infinity) - (a.margin_pct ?? -Infinity))
                .slice(0, 5),
        };

        return {
            totalOrders: orders.length,
            inProgress,
            completed,
            newOrdersThisMonth,
            active,
            uninvoiced,
            completedAgg,
            activeCustomers: activeCustomerIds.size,
            avgOrderValue12m,
            avgGrossProfit12m,
            avgUnitsPerOrder12m,
            avgOrdersPerMonth12m,
            totalRevenue12m,
            totalGrossProfit12m,
            totalUnits12m,
            totalCount12m,
            weightedMargin12m,
            completedMargin,
            recentMargin3m,
            trend,
            trendByInvoiceDate: invoiceTrend,
            // Forward-looking revenue forecast — see
            // ``resolveExpectedDeliveryDate`` for the inclusion /
            // exclusion rules. 3 calendar months starting from NEXT
            // month (so a run on the 1st-of-Jan doesn't surface a
            // "January forecast" overlapping with the actual-revenue
            // January bar). Shape per entry:
            //   { key, label, forecast_eur, orders_count, orders:[...] }
            // ``orders`` carries just enough to render the drill-down
            // dialog (id, order_number, title, expected date, EUR).
            revenueForecast: (() => {
                // Build the next-3-month buckets up front so unknown
                // keys fall through naturally.
                const buckets = [];
                for (let i = 1; i <= 3; i += 1) {
                    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
                    buckets.push({
                        key: monthKey(d),
                        label: monthLabel(d),
                        forecast_eur: 0,
                        orders_count: 0,
                        orders: [],
                    });
                }
                const byKey = Object.fromEntries(buckets.map((b) => [b.key, b]));
                // Window end — anything resolving BEYOND the last
                // forecast month is dropped (we only chart what we
                // can show, and extremely-long-dated orders would
                // distort the "next quarter" story anyway).
                const windowEnd = new Date(
                    now.getFullYear(),
                    now.getMonth() + 4,
                    1,
                );
                orders.forEach((o) => {
                    if (!FORECAST_STATUSES.has(o.status)) return;
                    // "Already recognized" — the actual-revenue
                    // engine uses invoice_total/booked state to
                    // recognise revenue. Any order already carrying
                    // a confirmed invoice is in actuals (whether or
                    // not it's paid), so we exclude it from the
                    // forecast to prevent the overlap called out in
                    // the brief. Draft invoices (not booked) stay
                    // in the forecast.
                    const invStatus = o.invoice_status || null;
                    const invEcon = o.invoice_economic_status || null;
                    if (
                        invStatus === 'booked'
                        || invStatus === 'paid'
                        || invEcon === 'booked_in_economic'
                    ) {
                        return;
                    }
                    const eur = resolveOrderRevenueEur(o);
                    if (!(eur > 0)) return;
                    const expected = resolveExpectedDeliveryDate(o, now);
                    if (!expected) return;
                    // Anchor to the first of the month so a date
                    // like "Jan 31" lands cleanly in the Jan bucket.
                    const monthStart = new Date(
                        expected.getFullYear(),
                        expected.getMonth(),
                        1,
                    );
                    // Dates earlier than next month fall into
                    // "overdue production" — those contribute to
                    // the IMMEDIATE next month so admins see them
                    // instead of silently dropping them (a plant
                    // slip never cancels revenue, it just pushes
                    // it forward).
                    const nextMonthStart = new Date(
                        now.getFullYear(),
                        now.getMonth() + 1,
                        1,
                    );
                    let bucketDate = monthStart;
                    if (monthStart < nextMonthStart) bucketDate = nextMonthStart;
                    if (bucketDate >= windowEnd) return;
                    const bk = monthKey(bucketDate);
                    const bucket = byKey[bk];
                    if (!bucket) return;
                    bucket.forecast_eur += eur;
                    bucket.orders_count += 1;
                    bucket.orders.push({
                        id: o.id,
                        order_number: o.order_number,
                        title: o.title,
                        catalogue_number: o.catalogue_number,
                        organization_name: o.organization_name,
                        customer_name: o.customer_name,
                        product_type: o.product_type,
                        expected_date: expected.toISOString(),
                        expected_source:
                            o?.supplier_confirmation?.customer_delivery_date
                            || o?.supplier_confirmation?.delivery_date
                                ? 'supplier_confirmed'
                                : (o?.requested_delivery_date
                                    && new Date(o.requested_delivery_date) >= now
                                    ? 'customer_requested'
                                    : 'lead_time_estimate'),
                        revenue_eur: eur,
                    });
                });
                // Sort each bucket's orders by expected date for a
                // readable drill-down.
                buckets.forEach((b) => {
                    b.orders.sort((a, b2) =>
                        new Date(a.expected_date) - new Date(b2.expected_date));
                });
                return buckets;
            })(),
            topByMetric,
            // Kept for backwards compatibility with any consumer still reading
            // `metrics.topCustomers` — mirrors the revenue-ranked variant.
            topCustomers: topByMetric.revenue,
        };
    }, [orders]);

    const fmt = (n) => n.toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const openBreakdown = (variant) => {
        setBreakdownVariant(variant);
        setBreakdownOpen(true);
    };

    return (
        <div className="min-h-screen bg-background" data-testid="overview-page">
            <Header />

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header row — matches Finance / Orders style */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                            Overview
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                            High-level business snapshot across all customers.
                        </p>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500">
                        <LayoutDashboard className="h-4 w-4" />
                        Business dashboard
                    </div>
                </div>

                {/* Global dashboard filters — Product + Metric.
                    These two selectors control the entire analytical view:
                    KPI row 2, Performance Trend, and Top Customers all follow
                    both. The operational KPIs (row 1) stay fixed. */}
                <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                    <div className="flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-white p-1 w-fit" data-testid="overview-category-filter">
                        <span className="px-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Product</span>
                        {[
                            { key: null,     label: 'All' },
                            { key: 'vinyl',  label: 'Vinyl' },
                            { key: 'cd',     label: 'CD' },
                            { key: 'dvd',    label: 'DVD' },
                            { key: 'bluray', label: 'Blu-ray' },
                        ].map((opt) => {
                            const active = (category ?? null) === opt.key;
                            return (
                                <button
                                    key={opt.label}
                                    type="button"
                                    onClick={() => setCategory(opt.key)}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                        active
                                            ? 'bg-slate-900 text-white shadow-sm'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                    }`}
                                    data-testid={`overview-category-${opt.key || 'all'}`}
                                    aria-pressed={active}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-white p-1 w-fit" data-testid="overview-metric-filter">
                        <span className="px-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Metric</span>
                        {[
                            { key: 'revenue',      label: 'Revenue' },
                            { key: 'gross_profit', label: 'Gross Profit' },
                            { key: 'margin',       label: 'Margin' },
                            { key: 'units',        label: 'Units' },
                            { key: 'orders',       label: 'Orders' },
                        ].map((opt) => {
                            const active = metric === opt.key;
                            return (
                                <button
                                    key={opt.key}
                                    type="button"
                                    onClick={() => setMetric(opt.key)}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                        active
                                            ? 'bg-slate-900 text-white shadow-sm'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                    }`}
                                    data-testid={`overview-metric-${opt.key}`}
                                    aria-pressed={active}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-24 text-slate-400">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                        Loading…
                    </div>
                ) : (
                    <>
                        {/* Row 1 — fixed operational KPIs. Never adapts to metric. */}
                        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
                            <KpiCard
                                icon={ShoppingBag}
                                iconBg="bg-slate-50"
                                iconColor="text-slate-700"
                                label="Total Orders"
                                value={metrics.totalOrders}
                                onClick={() => navigate('/admin')}
                                testId="total-orders-metric"
                            />
                            <KpiCard
                                icon={Clock}
                                iconBg="bg-amber-50"
                                iconColor="text-amber-600"
                                label="In Progress"
                                value={metrics.inProgress}
                                onClick={() => navigate('/admin?status=in_progress')}
                                testId="in-progress-metric"
                            />
                            <KpiCard
                                icon={CheckCircle}
                                iconBg="bg-emerald-50"
                                iconColor="text-emerald-600"
                                label="Completed"
                                value={metrics.completed}
                                onClick={() => navigate('/admin?status=completed')}
                                testId="completed-metric"
                            />
                            <KpiCard
                                icon={Sparkles}
                                iconBg="bg-purple-50"
                                iconColor="text-purple-600"
                                label="New Orders This Month"
                                value={metrics.newOrdersThisMonth}
                                sub={new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                                onClick={() => navigate('/admin')}
                                testId="new-orders-this-month-metric"
                            />
                            <KpiCard
                                icon={Users}
                                iconBg="bg-teal-50"
                                iconColor="text-teal-600"
                                label="Active Customers"
                                value={metrics.activeCustomers}
                                sub="≥ 1 active order"
                                onClick={() => navigate('/admin/companies')}
                                testId="active-customers-metric"
                            />
                        </div>

                        {/* Row 2 — three adaptive KPIs driven by the global
                            Metric selector. All numbers come straight from
                            the same `metrics` accumulator that powers the
                            Performance Trend and Top Customers, so the whole
                            view stays reconciled. */}
                        {(() => {
                            const eur = (v) => `€${fmt(v || 0)}`;
                            const pct = (v) => v == null ? '—' : `${v.toFixed(1)}%`;
                            const num = (v) => fmt(v || 0);
                            // eslint-disable-next-line react/prop-types
                            const Slot = ({ testId, icon: Icon, iconBg, iconColor, headline, sub, footer, onClick }) => (
                                <Card
                                    className={`metric-card ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
                                    onClick={onClick}
                                    role={onClick ? 'button' : undefined}
                                    data-testid={testId}
                                >
                                    <div className="flex items-center gap-2.5">
                                        <div className={`p-1.5 ${iconBg} rounded-lg`}>
                                            <Icon className={`h-4 w-4 ${iconColor}`} />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xl font-bold text-slate-900 leading-tight truncate">{headline}</p>
                                            <p className="text-[11px] text-muted-foreground leading-tight">{sub}</p>
                                            {footer && <p className="text-[10px] text-slate-500 mt-0.5">{footer}</p>}
                                        </div>
                                    </div>
                                </Card>
                            );

                            let slotA, slotB, slotC;
                            if (metric === 'revenue') {
                                slotA = <Slot testId="metric-slot-a" icon={FileText} iconBg="bg-indigo-50" iconColor="text-indigo-600"
                                    headline={`${fmt(metrics.active.dkk)} DKK`}
                                    sub={`Active Order Value · ${metrics.active.count} orders`}
                                    footer={`€${fmt(metrics.active.eur)} · click for breakdown →`}
                                    onClick={() => openBreakdown('active')} />;
                                slotB = <Slot testId="metric-slot-b" icon={AlertCircle} iconBg="bg-amber-50" iconColor="text-amber-600"
                                    headline={`${fmt(metrics.uninvoiced.dkk)} DKK`}
                                    sub={`Uninvoiced · ${metrics.uninvoiced.count} orders`}
                                    footer={`€${fmt(metrics.uninvoiced.eur)} · click for breakdown →`}
                                    onClick={() => openBreakdown('uninvoiced')} />;
                                slotC = <Slot testId="metric-slot-c" icon={Calculator} iconBg="bg-blue-50" iconColor="text-blue-600"
                                    headline={eur(metrics.avgOrderValue12m)}
                                    sub="Avg Order Value (12mo)"
                                    footer={`${metrics.totalCount12m} orders · €${fmt(metrics.totalRevenue12m)}`} />;
                            } else if (metric === 'gross_profit') {
                                slotA = <Slot testId="metric-slot-a" icon={FileText} iconBg="bg-cyan-50" iconColor="text-cyan-600"
                                    headline={eur(metrics.active.gross_profit_eur)}
                                    sub={`Active Gross Profit · ${metrics.active.costed_orders}/${metrics.active.count} costed`}
                                    footer={`Revenue €${fmt(metrics.active.eur)}`}
                                    onClick={() => openBreakdown('active')} />;
                                slotB = <Slot testId="metric-slot-b" icon={AlertCircle} iconBg="bg-amber-50" iconColor="text-amber-600"
                                    headline={eur(metrics.uninvoiced.gross_profit_eur)}
                                    sub={`Uninvoiced Gross Profit · ${metrics.uninvoiced.costed_orders}/${metrics.uninvoiced.count} costed`}
                                    footer={`Revenue €${fmt(metrics.uninvoiced.eur)}`}
                                    onClick={() => openBreakdown('uninvoiced')} />;
                                slotC = <Slot testId="metric-slot-c" icon={Calculator} iconBg="bg-cyan-50" iconColor="text-cyan-600"
                                    headline={eur(metrics.avgGrossProfit12m)}
                                    sub="Avg Gross Profit / Order (12mo)"
                                    footer={`GP €${fmt(metrics.totalGrossProfit12m)} · ${metrics.totalCount12m} orders`} />;
                            } else if (metric === 'margin') {
                                slotA = <Slot testId="metric-slot-a" icon={Percent} iconBg="bg-emerald-50" iconColor="text-emerald-600"
                                    headline={pct(metrics.weightedMargin12m)}
                                    sub="Weighted Gross Margin (12mo)"
                                    footer={`Σ €${fmt(metrics.totalGrossProfit12m)} / €${fmt(metrics.totalRevenue12m)}`} />;
                                slotB = <Slot testId="metric-slot-b" icon={CheckCircle} iconBg="bg-emerald-50" iconColor="text-emerald-600"
                                    headline={pct(metrics.completedMargin)}
                                    sub={`Margin on Completed Orders · ${metrics.completedAgg.count} orders`}
                                    footer={metrics.completedAgg.count === 0 ? 'No costed completed orders yet' : `GP €${fmt(metrics.completedAgg.gross_profit_eur)} / €${fmt(metrics.completedAgg.revenue_eur)}`} />;
                                slotC = <Slot testId="metric-slot-c" icon={TrendingUp} iconBg="bg-emerald-50" iconColor="text-emerald-600"
                                    headline={pct(metrics.recentMargin3m)}
                                    sub="Recent Margin (3mo)"
                                    footer={metrics.weightedMargin12m != null && metrics.recentMargin3m != null
                                        ? `${metrics.recentMargin3m >= metrics.weightedMargin12m ? '↑' : '↓'} ${(metrics.recentMargin3m - metrics.weightedMargin12m).toFixed(1)}pt vs 12mo`
                                        : 'Trend vs 12mo'} />;
                            } else if (metric === 'units') {
                                slotA = <Slot testId="metric-slot-a" icon={ShoppingBag} iconBg="bg-amber-50" iconColor="text-amber-600"
                                    headline={`${num(metrics.totalUnits12m)} units`}
                                    sub={`Total Units (12mo) · ${metrics.totalCount12m} orders`} />;
                                slotB = <Slot testId="metric-slot-b" icon={Clock} iconBg="bg-amber-50" iconColor="text-amber-600"
                                    headline={`${num(metrics.active.units)} units`}
                                    sub={`Units in Progress · ${metrics.active.count} active orders`} />;
                                slotC = <Slot testId="metric-slot-c" icon={Calculator} iconBg="bg-blue-50" iconColor="text-blue-600"
                                    headline={num(Math.round(metrics.avgUnitsPerOrder12m))}
                                    sub="Avg Units per Order (12mo)" />;
                            } else /* orders */ {
                                slotA = <Slot testId="metric-slot-a" icon={ShoppingBag} iconBg="bg-purple-50" iconColor="text-purple-600"
                                    headline={num(metrics.totalCount12m)}
                                    sub="Orders (12mo)"
                                    footer={`€${fmt(metrics.totalRevenue12m)} revenue`} />;
                                slotB = <Slot testId="metric-slot-b" icon={AlertCircle} iconBg="bg-amber-50" iconColor="text-amber-600"
                                    headline={num(metrics.uninvoiced.count)}
                                    sub="Uninvoiced Orders"
                                    footer={`€${fmt(metrics.uninvoiced.eur)} value`}
                                    onClick={() => openBreakdown('uninvoiced')} />;
                                slotC = <Slot testId="metric-slot-c" icon={Calculator} iconBg="bg-purple-50" iconColor="text-purple-600"
                                    headline={metrics.avgOrdersPerMonth12m.toFixed(1)}
                                    sub="Avg Orders / Month (12mo)" />;
                            }
                            return (
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4" data-testid="metric-kpi-row">
                                    {slotA}
                                    {slotB}
                                    {slotC}
                                </div>
                            );
                        })()}

                        {/* Performance Trend + Top Customers side-by-side.
                            Metric selector lives inside the card so the page
                            keeps a single global filter row above. */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                            <Card className="p-4 lg:col-span-2" data-testid="performance-trend-card">
                                {(() => {
                                    const METRICS = {
                                        revenue: {
                                            label: 'Revenue',
                                            key: 'revenue_eur',
                                            axis: 'eur',
                                            color: '#6366f1',
                                            subtitle: 'Net revenue by month · last 12 months',
                                            tooltipLabel: 'Revenue',
                                            tooltipFmt: (v) => `€${fmt(v)}`,
                                        },
                                        gross_profit: {
                                            label: 'Gross Profit',
                                            key: 'gross_profit_eur',
                                            axis: 'eur',
                                            color: '#0891b2',
                                            subtitle: 'Gross profit by month · last 12 months',
                                            tooltipLabel: 'Gross Profit',
                                            tooltipFmt: (v) => `€${fmt(v)}`,
                                        },
                                        margin: {
                                            label: 'Margin',
                                            key: 'margin_pct',
                                            axis: 'pct',
                                            color: '#10b981',
                                            subtitle: 'Revenue-weighted gross margin by month · last 12 months',
                                            tooltipLabel: 'Gross Margin',
                                            tooltipFmt: (v) => (v == null ? '—' : `${v.toFixed(1)}%`),
                                        },
                                        units: {
                                            label: 'Units',
                                            key: 'units',
                                            axis: 'count',
                                            color: '#f59e0b',
                                            subtitle: 'Units by month · last 12 months',
                                            tooltipLabel: 'Units',
                                            tooltipFmt: (v) => fmt(v),
                                        },
                                        orders: {
                                            label: 'Orders',
                                            key: 'orders_count',
                                            axis: 'count',
                                            color: '#8b5cf6',
                                            subtitle: 'Orders by month · last 12 months',
                                            tooltipLabel: 'Orders',
                                            tooltipFmt: (v) => fmt(v),
                                        },
                                    };
                                    const m = METRICS[metric] || METRICS.revenue;
                                    // Which trend series feeds the chart —
                                    // switches with the date-basis toggle.
                                    // `trendByInvoiceDate` skips orders that
                                    // don't have a reliable invoice date
                                    // (see backend resolver).
                                    const trendData = dateBasis === 'invoice_date'
                                        ? metrics.trendByInvoiceDate
                                        : metrics.trend;
                                    // Append the 3-month revenue
                                    // forecast as additional rows on
                                    // the X-axis. ``revenue_eur`` is
                                    // null on forecast rows so the
                                    // actual-revenue series doesn't
                                    // extend into the future, and
                                    // ``forecast_eur`` is null on the
                                    // 12 historical rows so the
                                    // dashed line only paints the
                                    // next-3-month segment. Only
                                    // layered onto the chart for the
                                    // ``revenue`` metric — all other
                                    // metrics (margin / orders /
                                    // units / profit) keep their
                                    // existing look.
                                    const showForecast = metric === 'revenue';
                                    const forecastRows = showForecast
                                        ? (metrics.revenueForecast || []).map((f) => ({
                                            key: f.key,
                                            label: f.label,
                                            revenue_eur: null,
                                            orders_count: null,
                                            gross_profit_eur: null,
                                            units: null,
                                            margin_pct: null,
                                            forecast_eur: f.forecast_eur,
                                            forecast_orders: f.orders_count,
                                            is_forecast: true,
                                        }))
                                        : [];
                                    const trendDataWithForecast = showForecast
                                        ? [...trendData, ...forecastRows]
                                        : trendData;
                                    // Bridge actual→forecast so the
                                    // dashed line visually continues
                                    // from the last real month. We
                                    // copy the latest actual value
                                    // into ``forecast_eur`` on the
                                    // final historical row so the
                                    // Recharts line segment has a
                                    // starting point.
                                    if (showForecast && trendData.length > 0) {
                                        const lastActual = trendData[trendData.length - 1];
                                        trendDataWithForecast[trendData.length - 1] = {
                                            ...lastActual,
                                            forecast_eur: lastActual.revenue_eur || 0,
                                        };
                                    }
                                    const forecastByKey = Object.fromEntries(
                                        (metrics.revenueForecast || []).map((f) => [f.key, f]),
                                    );
                                    const basisSuffix = dateBasis === 'invoice_date'
                                        ? ' · by invoice date'
                                        : ' · by order date';
                                    const yTickFmt = m.axis === 'eur'
                                        ? (v) => `€${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`
                                        : m.axis === 'pct'
                                            ? (v) => `${Math.round(v)}%`
                                            : (v) => `${v}`;
                                    const isLine = m.axis === 'pct';
                                    const yDomain = m.axis === 'pct'
                                        ? [0, (max) => Math.max(60, Math.ceil((max || 0) / 10) * 10)]
                                        : undefined;
                                    // Hide the whole datapoint row when the
                                    // selected metric is null (relevant for
                                    // Margin — months with no valid orders).
                                    const anyData = trendData.some((r) => (r[m.key] ?? 0) !== 0);
                                    const anyForecast = showForecast
                                        && (metrics.revenueForecast || []).some((f) => (f.forecast_eur || 0) > 0);
                                    // Custom tooltip so forecast rows
                                    // can carry their own copy — the
                                    // user needs to know the dashed
                                    // segment is an estimate, with
                                    // the contributing order count.
                                    const chartTooltipContent = ({ active, payload, label }) => {
                                        if (!active || !payload || payload.length === 0) return null;
                                        const row = payload[0]?.payload;
                                        if (!row) return null;
                                        if (row.is_forecast) {
                                            return (
                                                <div className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-md" data-testid="performance-trend-forecast-tooltip">
                                                    <div className="font-semibold text-slate-900">{label}</div>
                                                    <div className="text-slate-700">
                                                        {m.tooltipFmt(row.forecast_eur)} <span className="text-slate-500">forecast</span>
                                                    </div>
                                                    <div className="text-[11px] text-slate-500 mt-0.5">
                                                        {row.forecast_orders} contributing order{row.forecast_orders === 1 ? '' : 's'}
                                                    </div>
                                                    <div className="text-[10px] text-amber-700 mt-0.5 italic">
                                                        Estimated — not guaranteed
                                                    </div>
                                                </div>
                                            );
                                        }
                                        const value = row[m.key];
                                        return (
                                            <div className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-md">
                                                <div className="font-semibold text-slate-900">{label}</div>
                                                <div className="text-slate-700">
                                                    {m.tooltipFmt(value)} <span className="text-slate-500">{m.tooltipLabel.toLowerCase()}</span>
                                                </div>
                                            </div>
                                        );
                                    };
                                    const handleForecastDotClick = (dotProps) => {
                                        const row = dotProps?.payload;
                                        if (!row?.is_forecast) return;
                                        const bucket = forecastByKey[row.key];
                                        if (bucket && (bucket.orders_count || 0) > 0) {
                                            setForecastDialogBucket(bucket);
                                        }
                                    };
                                    return (
                                        <>
                                            <div className="flex items-center justify-between gap-2 mb-2">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <TrendingUp className="h-4 w-4 text-slate-600 flex-shrink-0" />
                                                    <div className="min-w-0">
                                                        <h2 className="text-sm font-semibold text-slate-900">Performance Trend</h2>
                                                        <p className="text-[11px] text-slate-500 truncate" data-testid="performance-trend-subtitle">
                                                            {m.subtitle}{basisSuffix}
                                                        </p>
                                                    </div>
                                                </div>
                                                {/* Date-basis toggle. Mirrors the visual language
                                                    of the page-level Metric selector — segmented
                                                    control, active pill highlighted. Order-date is
                                                    the default; Invoice-date buckets by the real
                                                    invoice date (see resolver in orders.py). */}
                                                <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 flex-shrink-0" data-testid="performance-trend-basis-toggle">
                                                    {[
                                                        { key: 'order_date', label: 'Order date' },
                                                        { key: 'invoice_date', label: 'Invoice date' },
                                                    ].map((opt) => (
                                                        <button
                                                            key={opt.key}
                                                            type="button"
                                                            onClick={() => setDateBasis(opt.key)}
                                                            className={
                                                                'px-2.5 py-1 text-[11px] font-medium rounded-[5px] transition-colors ' +
                                                                (dateBasis === opt.key
                                                                    ? 'bg-slate-900 text-white'
                                                                    : 'text-slate-600 hover:text-slate-900')
                                                            }
                                                            data-testid={`performance-trend-basis-${opt.key}`}
                                                        >
                                                            {opt.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="h-56 w-full" data-testid="performance-trend-chart">
                                                {!anyData ? (
                                                    <div className="h-full flex items-center justify-center text-xs text-slate-400 px-4 text-center" data-testid="performance-trend-empty">
                                                        {dateBasis === 'invoice_date'
                                                            ? 'No invoice-dated orders in the last 12 months for the selected category.'
                                                            : `No ${m.label.toLowerCase()} data in the last 12 months for the selected category.`}
                                                    </div>
                                                ) : (
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        {isLine ? (
                                                            <LineChart data={trendData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                                                                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                                                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                                                                <YAxis
                                                                    tick={{ fontSize: 11, fill: '#64748b' }}
                                                                    axisLine={{ stroke: '#e2e8f0' }}
                                                                    tickLine={false}
                                                                    tickFormatter={yTickFmt}
                                                                    width={48}
                                                                    domain={yDomain}
                                                                />
                                                                <Tooltip
                                                                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                                                                    formatter={(v) => [m.tooltipFmt(v), m.tooltipLabel]}
                                                                    labelStyle={{ color: '#0f172a', fontWeight: 600 }}
                                                                />
                                                                <Line type="monotone" dataKey={m.key} stroke={m.color} strokeWidth={2} dot={{ r: 3, fill: m.color }} connectNulls />
                                                            </LineChart>
                                                        ) : (
                                                            <ComposedChart data={trendDataWithForecast} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                                                                <defs>
                                                                    <linearGradient id="performanceGradient" x1="0" y1="0" x2="0" y2="1">
                                                                        <stop offset="0%" stopColor={m.color} stopOpacity={0.35} />
                                                                        <stop offset="100%" stopColor={m.color} stopOpacity={0} />
                                                                    </linearGradient>
                                                                </defs>
                                                                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                                                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                                                                <YAxis
                                                                    tick={{ fontSize: 11, fill: '#64748b' }}
                                                                    axisLine={{ stroke: '#e2e8f0' }}
                                                                    tickLine={false}
                                                                    tickFormatter={yTickFmt}
                                                                    width={48}
                                                                />
                                                                <Tooltip
                                                                    content={chartTooltipContent}
                                                                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                                                                />
                                                                <Area type="monotone" dataKey={m.key} stroke={m.color} strokeWidth={2} fill="url(#performanceGradient)" />
                                                                {showForecast && anyForecast && (
                                                                    <Line
                                                                        type="monotone"
                                                                        dataKey="forecast_eur"
                                                                        stroke="#64748b"
                                                                        strokeWidth={2}
                                                                        strokeDasharray="4 4"
                                                                        dot={(props) => {
                                                                            const row = props?.payload;
                                                                            if (!row?.is_forecast) {
                                                                                return <g key={`fcd-${props.cx}-${props.cy}`} />;
                                                                            }
                                                                            return (
                                                                                <circle
                                                                                    key={`fcd-${props.cx}-${props.cy}`}
                                                                                    cx={props.cx}
                                                                                    cy={props.cy}
                                                                                    r={3.5}
                                                                                    fill="#64748b"
                                                                                    stroke="#fff"
                                                                                    strokeWidth={1}
                                                                                    style={{ cursor: 'pointer' }}
                                                                                    onClick={() => handleForecastDotClick(props)}
                                                                                    data-testid={`performance-trend-forecast-dot-${row.key}`}
                                                                                />
                                                                            );
                                                                        }}
                                                                        activeDot={{
                                                                            r: 5,
                                                                            fill: '#64748b',
                                                                            stroke: '#fff',
                                                                            strokeWidth: 2,
                                                                            cursor: 'pointer',
                                                                            onClick: (_, dotProps) => handleForecastDotClick(dotProps),
                                                                        }}
                                                                        isAnimationActive={false}
                                                                        connectNulls
                                                                    />
                                                                )}
                                                            </ComposedChart>
                                                        )}
                                                    </ResponsiveContainer>
                                                )}
                                            </div>
                                            {showForecast && anyForecast && (
                                                <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-500" data-testid="performance-trend-forecast-legend">
                                                    <div className="flex items-center gap-3">
                                                        <span className="inline-flex items-center gap-1">
                                                            <span className="inline-block w-3 h-0.5 rounded" style={{ backgroundColor: m.color }} />
                                                            Actual
                                                        </span>
                                                        <span className="inline-flex items-center gap-1">
                                                            <span className="inline-block w-3 h-px border-t-2 border-dashed border-slate-500" />
                                                            Forecast (next 3 months)
                                                        </span>
                                                    </div>
                                                    <span className="italic text-amber-700">Forecast is estimated, not guaranteed</span>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </Card>

                            {/* Top Customers — follows both global selectors.
                                Ranking, primary value and supporting context
                                all come from the same `topByMetric` bucket
                                that lives on `metrics`, so the view is
                                always reconciled with the KPI row and the
                                Performance Trend. */}
                            <Card className="p-4" data-testid="top-customers-card">
                                {(() => {
                                    const TC = {
                                        revenue:      { label: 'Top 5 by revenue · last 12 months' },
                                        gross_profit: { label: 'Top 5 by gross profit · last 12 months' },
                                        margin:       { label: 'Top 5 by gross margin · last 12 months' },
                                        units:        { label: 'Top 5 by units · last 12 months' },
                                        orders:       { label: 'Top 5 by orders · last 12 months' },
                                    };
                                    const list = metrics.topByMetric[metric] || [];
                                    const primary = (c) => {
                                        switch (metric) {
                                            case 'revenue':      return `€${fmt(c.revenue_eur)}`;
                                            case 'gross_profit': return `€${fmt(c.gross_profit_eur)}`;
                                            case 'margin':       return c.margin_pct == null ? '—' : `${c.margin_pct.toFixed(1)}%`;
                                            case 'units':        return `${fmt(c.units)} units`;
                                            case 'orders':       return `${c.orders} orders`;
                                            default:             return '';
                                        }
                                    };
                                    const context = (c) => {
                                        switch (metric) {
                                            case 'revenue':      return `${c.orders} orders`;
                                            case 'gross_profit': return `${c.orders} orders · €${fmt(c.revenue_eur)} revenue`;
                                            case 'margin':       return `${c.orders} orders · €${fmt(c.revenue_eur)} rev · €${fmt(c.gross_profit_eur)} GP`;
                                            case 'units':        return `${c.orders} orders`;
                                            case 'orders':       return `€${fmt(c.revenue_eur)} revenue`;
                                            default:             return '';
                                        }
                                    };
                                    const emptyMsg = metric === 'margin'
                                        ? 'No customer with ≥2 costed orders and ≥€2 500 revenue in the last 12 months.'
                                        : `No customer data for ${TC[metric].label.toLowerCase().replace('top 5 by ', '')} in the last 12 months.`;
                                    return (
                                        <>
                                            <div className="flex items-center gap-2 mb-2">
                                                <Trophy className="h-4 w-4 text-amber-500" />
                                                <div>
                                                    <h2 className="text-sm font-semibold text-slate-900">Top Customers</h2>
                                                    <p className="text-[11px] text-slate-500" data-testid="top-customers-subtitle">{TC[metric].label}</p>
                                                </div>
                                            </div>
                                            {list.length === 0 ? (
                                                <div className="py-6 text-center text-xs text-slate-400" data-testid="top-customers-empty">{emptyMsg}</div>
                                            ) : (
                                                <ul className="divide-y divide-slate-100">
                                                    {list.map((c, i) => (
                                                        <li key={c.id || c.name || i}>
                                                            <button
                                                                type="button"
                                                                onClick={() => navigate(c.id ? `/admin?customer=${c.id}` : '/admin')}
                                                                className="w-full flex items-center gap-2 py-2 text-left hover:bg-slate-50 -mx-1 px-1 rounded transition-colors"
                                                                data-testid={`top-customer-${i}`}
                                                            >
                                                                <span className="flex-shrink-0 h-5 w-5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold flex items-center justify-center">
                                                                    {i + 1}
                                                                </span>
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="text-sm font-medium text-slate-900 truncate">{c.name}</p>
                                                                    <p className="text-[11px] text-slate-500 truncate">{context(c)}</p>
                                                                </div>
                                                                <p className="text-sm font-semibold text-slate-900 tabular-nums whitespace-nowrap">
                                                                    {primary(c)}
                                                                </p>
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </>
                                    );
                                })()}
                            </Card>
                        </div>

                        <Card className="bg-slate-50 border-dashed">
                            <div className="p-3 text-[11px] text-slate-500 text-center">
                                More insights coming soon — customer growth, production timings, cash flow.
                            </div>
                        </Card>
                    </>
                )}

                <OrderValueBreakdownDialog
                    open={breakdownOpen}
                    onOpenChange={setBreakdownOpen}
                    metric={breakdownVariant === 'active' ? metrics.active : metrics.uninvoiced}
                    variant={breakdownVariant}
                />

                {/* Forecast drill-down — contributing orders for the
                    clicked next-month bucket. Open path:
                    forecast dot click → setForecastDialogBucket(). */}
                <Dialog
                    open={!!forecastDialogBucket}
                    onOpenChange={(open) => { if (!open) setForecastDialogBucket(null); }}
                >
                    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="performance-trend-forecast-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <TrendingUp className="h-4 w-4 text-slate-600" />
                                Forecast · {forecastDialogBucket?.label}
                            </DialogTitle>
                        </DialogHeader>
                        {forecastDialogBucket && (
                            <div className="space-y-3">
                                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                    <strong>Estimate only.</strong> Forecast revenue is derived
                                    from confirmed orders using supplier delivery dates when
                                    available, else customer-requested delivery (if still
                                    realistic), else production lead time. Actual bookings may
                                    differ.
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-xs text-slate-600">
                                    <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Expected revenue</div>
                                        <div className="text-sm font-semibold text-slate-900">
                                            €{(forecastDialogBucket.forecast_eur || 0).toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                        </div>
                                    </div>
                                    <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Contributing orders</div>
                                        <div className="text-sm font-semibold text-slate-900">
                                            {forecastDialogBucket.orders_count}
                                        </div>
                                    </div>
                                    <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Month</div>
                                        <div className="text-sm font-semibold text-slate-900">
                                            {forecastDialogBucket.label}
                                        </div>
                                    </div>
                                </div>
                                {forecastDialogBucket.orders.length === 0 ? (
                                    <div className="py-4 text-center text-sm text-slate-500">
                                        No contributing orders.
                                    </div>
                                ) : (
                                    <div className="rounded-lg border border-slate-200 overflow-hidden">
                                        <table className="w-full text-xs" data-testid="performance-trend-forecast-orders">
                                            <thead className="bg-slate-50 text-slate-500">
                                                <tr>
                                                    <th className="text-left px-3 py-2 font-medium">Order</th>
                                                    <th className="text-left px-3 py-2 font-medium">Customer</th>
                                                    <th className="text-left px-3 py-2 font-medium">Expected</th>
                                                    <th className="text-left px-3 py-2 font-medium">Basis</th>
                                                    <th className="text-right px-3 py-2 font-medium">EUR</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 bg-white">
                                                {forecastDialogBucket.orders.map((o) => (
                                                    <tr
                                                        key={o.id}
                                                        className="hover:bg-slate-50 cursor-pointer"
                                                        onClick={() => { navigate(`/orders/${o.id}`); }}
                                                    >
                                                        <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                                                            #{o.order_number || '—'}
                                                            {o.catalogue_number && (
                                                                <span className="text-slate-400"> · {o.catalogue_number}</span>
                                                            )}
                                                            {o.title && (
                                                                <div className="text-[10px] text-slate-500">{o.title}</div>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2 text-slate-700">
                                                            {o.organization_name || o.customer_name || '—'}
                                                        </td>
                                                        <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                                                            {new Date(o.expected_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                        </td>
                                                        <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                                                            {o.expected_source === 'supplier_confirmed' && 'Supplier confirmed'}
                                                            {o.expected_source === 'customer_requested' && 'Customer requested'}
                                                            {o.expected_source === 'lead_time_estimate' && `Est. ${o.product_type || 'order'} lead time`}
                                                        </td>
                                                        <td className="px-3 py-2 text-right font-medium text-slate-700 whitespace-nowrap">
                                                            €{(o.revenue_eur || 0).toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </main>
        </div>
    );
};

export default OverviewPage;
